import { useEffect, useState } from "react";
import { useSettings } from "@/context/SettingsContext";
import { Plus, DollarSign, Calendar, CreditCard, Receipt as ReceiptIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/utils/api";

const Payments = () => {
  const { formatDate, formatCurrency, settings } = useSettings();
  const [payments, setPayments] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    member_id: "",
    membership_id: "",
    amount: "",
    method: "cash",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, membersRes, membershipsRes] = await Promise.all([
        api.get('/payments'),
        api.get('/members'),
        api.get('/memberships'),
      ]);
      setPayments(paymentsRes.data);
      setMembers(membersRes.data);
      setMemberships(membershipsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/payments', {
        member_id: formData.member_id,
        membership_id: formData.membership_id || null,
        amount: parseFloat(formData.amount),
        method: formData.method,
      });
      toast.success("Payment recorded successfully");
      fetchData();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error("Failed to record payment");
    }
  };

  const resetForm = () => {
    setFormData({
      member_id: "",
      membership_id: "",
      amount: "",
      method: "cash",
    });
  };

  const getMemberName = (memberId) => {
    const member = members.find((m) => m.id === memberId);
    return member ? member.name : "Unknown";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-400"></div>
      </div>
    );
  }

  return (
    <div data-testid="payments-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Payments
          </h1>
          <p className="text-zinc-500">Record and manage payments</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              data-testid="add-payment-btn"
              className="bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold shadow-sm transition-all active:scale-95"
            >
              <Plus size={20} className="mr-2" />
              Record Payment
            </Button>
          </DialogTrigger>
          <DialogContent aria-describedby="payment-dialog-description">
            <DialogHeader>
              <DialogTitle>Record New Payment</DialogTitle>
            </DialogHeader>
            <p id="payment-dialog-description" className="sr-only">
              Record a new payment transaction for a member
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="member">Select Member</Label>
                <Select
                  value={formData.member_id}
                  onValueChange={(value) => setFormData({ ...formData, member_id: value })}
                  required
                >
                  <SelectTrigger data-testid="payment-member-select">
                    <SelectValue placeholder="Choose a member" />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="amount">Amount ($)</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  data-testid="payment-amount-input"
                  placeholder="49.99"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="method">Payment Method</Label>
                <Select
                  value={formData.method}
                  onValueChange={(value) => setFormData({ ...formData, method: value })}
                >
                  <SelectTrigger data-testid="payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button 
                type="submit" 
                data-testid="payment-submit-btn"
                className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold"
                disabled={!formData.member_id || !formData.amount}
              >
                Record Payment
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Payments Table */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden shadow-sm">
        <table className="w-full">
          <thead className="bg-zinc-50 text-zinc-500 font-medium text-xs uppercase tracking-wider">
            <tr>
              <th className="px-6 py-4 text-left">Receipt #</th>
              <th className="px-6 py-4 text-left">Member</th>
              <th className="px-6 py-4 text-left">Amount</th>
              <th className="px-6 py-4 text-left">Method</th>
              <th className="px-6 py-4 text-left">Date</th>
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center text-zinc-500">
                  No payments recorded yet. Start recording payments.
                </td>
              </tr>
            ) : (
              payments.map((payment) => (
                <tr
                  key={payment.id}
                  data-testid={`payment-row-${payment.id}`}
                  className="hover:bg-zinc-50 transition-colors border-b border-zinc-100 last:border-0"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm font-mono text-zinc-600">
                      <ReceiptIcon size={14} />
                      {payment.receipt_number}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-zinc-900">{getMemberName(payment.member_id)}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-zinc-900 font-bold">
                      {formatCurrency(payment.amount)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm text-zinc-600">
                      <CreditCard size={14} />
                      <span className="capitalize">{payment.method.replace('_', ' ')}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm text-zinc-600">
                      <Calendar size={14} />
                      {formatDate(payment.payment_date)}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Payments;